import { useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth, TwoFactorRequiredError, TwoFactorSetupRequiredError } from '@repletoryreach/sdk-auth';
import { Stack, Button, Text } from '@repletoryreach/sdk-ui';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { TextInput, PasswordInput, Checkbox } from '@repletoryreach/sdk-ui/inputs';
import { isApiError } from '@repletoryreach/sdk-api';
import { useT, type Translate } from '../../../../i18n/I18nProvider';
import { TENANT_CONSOLE_URL } from '../../../../app/external-links';
import { MailIcon, LockIcon } from '../../components/icons';

/** Where the last signed-in address is kept, so the field can be prefilled. */
const REMEMBERED_EMAIL_KEY = 'rr.admin.rememberedEmail';

function readRememberedEmail(): string {
  try {
    return window.localStorage.getItem(REMEMBERED_EMAIL_KEY) ?? '';
  } catch {
    return '';
  }
}

/**
 * Turn a failed sign-in into a sentence.
 *
 * Anything that was not a 2FA challenge or a wrong-domain refusal used to fall
 * through to `err.message`, which for a rejected password is whatever the
 * transport put there ("HTTP 401"). Identity answers a bad password and a
 * locked-out account with the same bare 401, so the copy covers both: it names
 * the likely cause and says that repeated attempts lock the account.
 */
function messageFor(err: unknown, t: Translate): string {
  if (isApiError(err)) {
    if (err.status === 401 || err.code === 'INVALID_CREDENTIALS') return t('auth.login.invalidCredentials');
    if (err.status === 403) return t('auth.login.wrongDomain');
    if (err.status >= 500) return t('auth.login.networkError');
  }
  if (err instanceof Error && err.name === 'NetworkError') return t('auth.login.networkError');
  return t('auth.login.failed');
}

export function LoginForm() {
  const t = useT();
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const remembered = readRememberedEmail();
  const [email, setEmail] = useState(remembered);
  const [password, setPassword] = useState('');
  // The reference design puts a "Remember me" box here. Nothing in sdk-auth
  // varies the session's lifetime, so a box promising a longer session would
  // be a lie; what this one does is exactly what it says, which is to keep the
  // address on this browser so the next sign-in starts at the password field.
  const [rememberEmail, setRememberEmail] = useState(remembered !== '');
  const [error, setError] = useState<string | null>(null);
  const [wrongDomain, setWrongDomain] = useState(false);
  const [busy, setBusy] = useState(false);

  // The guard records the path that sent the operator here, so a bookmarked
  // deep link survives the trip through sign-in instead of always landing on
  // the dashboard.
  const from = (location.state as { from?: string } | null)?.from ?? '/';

  function persistEmail() {
    try {
      if (rememberEmail) window.localStorage.setItem(REMEMBERED_EMAIL_KEY, email);
      else window.localStorage.removeItem(REMEMBERED_EMAIL_KEY);
    } catch { /* storage unavailable: the prefill is a convenience, not a requirement */ }
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setWrongDomain(false);
    setBusy(true);
    try {
      await login(email, password);
      persistEmail();
      navigate(from, { replace: true });
    } catch (err) {
      if (err instanceof TwoFactorSetupRequiredError) {
        // Invited staff set up two-factor before the first session (3 October 2026).
        persistEmail();
        navigate('/auth/mfa-setup', { replace: true, state: { from } });
        return;
      }
      if (err instanceof TwoFactorRequiredError) {
        // The challenge is its own screen. `challenge` tells it the pending
        // token exists; landing on /auth/mfa cold has nothing to complete.
        persistEmail();
        navigate('/auth/mfa', { replace: true, state: { challenge: true, from } });
        return;
      }
      if (isApiError(err) && err.code === 'WRONG_DOMAIN') {
        setWrongDomain(true);
        setError(t('auth.login.wrongDomain'));
      } else {
        setError(messageFor(err, t));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      <Stack gap="md">
        {/* A screen reader got nothing at all when a sign-in failed: the alert
            simply appeared in the DOM. The live region announces it. */}
        <div role="alert" aria-live="assertive">
          {error && (
            <Alert color="error" title={error}>
              <Stack gap="xs" align="flex-start">
                {wrongDomain && TENANT_CONSOLE_URL !== '' && (
                  <a href={TENANT_CONSOLE_URL} style={{ color: 'var(--rr-color-primary)', fontSize: 13 }}>
                    {t('auth.layout.wrongDomainLink')}
                  </a>
                )}
                {!wrongDomain && (
                  <Text fz="sm">{t('auth.login.lockoutHint')}</Text>
                )}
              </Stack>
            </Alert>
          )}
        </div>

        <TextInput
          label={t('common.email')}
          leftSection={<MailIcon />}
          type="email"
          placeholder={t('auth.login.emailPlaceholder')}
          value={email}
          onChange={(e) => setEmail(e.currentTarget.value)}
          required
          autoComplete="email"
          autoFocus={remembered === ''}
        />
        <PasswordInput
          label={t('auth.login.form.password')}
          leftSection={<LockIcon />}
          placeholder={t('auth.login.passwordPlaceholder')}
          value={password}
          onChange={(e) => setPassword(e.currentTarget.value)}
          required
          autoComplete="current-password"
          autoFocus={remembered !== ''}
        />

        <div className="admin-auth__form-row">
          <Checkbox
            label={t('auth.login.rememberEmail')}
            checked={rememberEmail}
            onChange={setRememberEmail}
          />
          <Link to="/auth/forgot-password" style={{ color: 'var(--rr-color-primary)', fontSize: 13 }}>
            {t('auth.login.forgotLink')}
          </Link>
        </div>

        <Button type="submit" loading={busy} fullWidth size="md">
          {t('action.signIn')}
        </Button>
      </Stack>
    </form>
  );
}
